import {
  useCreateBillingPaymentMethodsSetDefaultByPaymentMethodId,
  useCreateBillingPaymentMethodsSetupIntent,
  useGetAuthMe,
  useGetBillingPaymentMethodsByPaymentMethodId as usePaymentMethodDetail,
} from "@birdwatch/client";
import {
  BlockScroll,
  Modal,
  ModalClose,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { capitalize, startCase } from "lodash-es";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { env } from "@/env";
import * as pmToast from "@/features/shared/billing/payments/_libs/payment-method-toasts";
import { PaymentMethodType } from "@/features/shared/billing/payments/_types/payment-method-type";
import { useRefresh } from "@/features/shared/realtime-refresh";
import { useStripeAppearance } from "@/hooks/use-stripe-appearance";

import type { PaymentDefaultActionScope } from "../_types/payment-default-scope";
import { PaymentMethodCard } from "./payment-method-card";
import {
  PaymentMethodFormContent,
  type SuccessData,
} from "./payment-method-form-content";
import { SetAsDefaultDialog } from "./set-as-default-dialog";

const stripePromise = loadStripe(env.PUBLIC_STRIPE_PUBLISHABLE_KEY);

type PaymentMethodSetupDialogProps = {
  /** Whether the dialog is visible. */
  open: boolean;

  /** Callback to toggle dialog visibility. */
  onOpenChange: (open: boolean) => void;

  /** Which payment method tab to show first. */
  defaultMethod?: PaymentMethodType;

  /** Whether this is the user's first payment method. */
  isFirstMethod: boolean;

  /** Whether adding a new or editing an existing payment method. */
  mode?: "add" | "edit";

  /** Optional callback overriding the default success behavior. */
  onSuccess?: (data: SuccessData) => void;

  /**
   * Optional callback for a Stripe setup failure, passed straight to the form.
   *
   * Without it the failure stays console-only, as it always has — so a caller that wants
   * to observe a declined card (the presale funnel reports
   * `Presale.PaymentMethodAddFailed`) has to ask for it.
   */
  onError?: (error: { message?: string }) => void;
};

/**
 * A responsive dialog that wraps the Stripe payment method form. Manages the SetupIntent lifecycle,
 * shows loading/error states, and transitions to a success view after the payment method is added.
 *
 * @param open - Whether the dialog is visible.
 * @param onOpenChange - Callback to toggle dialog visibility.
 * @param defaultMethod - Which payment method tab to show first.
 * @param isFirstMethod - Whether this is the user's first payment method.
 * @param mode - Whether adding a new or editing an existing payment method.
 * @param onSuccess - Optional callback overriding the default success behavior.
 * @param onError - Optional callback for a Stripe setup failure.
 */
export const PaymentMethodSetupDialog = ({
  open,
  onOpenChange,
  defaultMethod = PaymentMethodType.Card,
  isFirstMethod,
  mode = "add",
  onSuccess: onSuccessProp,
  onError,
}: PaymentMethodSetupDialogProps) => {
  const refresh = useRefresh();
  const { data: me } = useGetAuthMe().get();
  const accountId = me?.accounts?.[0]?.id ?? "";
  const appearance = useStripeAppearance();
  // `publish: false` route — it only mints a Stripe setup intent for the form.
  const { mutateAsync: createSetupIntent, isPending: isCreating } =
    useCreateBillingPaymentMethodsSetupIntent();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const [wasFirstMethod, setWasFirstMethod] = useState(isFirstMethod);

  // The SetupIntent lifecycle keys off `open` alone: closing drops the intent, opening mints
  // one whenever there isn't one. A SetupIntent is single-use, so re-opening on the secret
  // `confirmSetup` already consumed would mount an Elements form that cannot confirm.
  //
  // The cleanup lives here rather than in a close callback because Radix/Vaul report
  // `onOpenChange` only for a user-initiated close (Esc, backdrop, ModalClose) — never when a
  // caller flips `open` itself, which is what a caller with its own `onSuccess` does once it
  // has run its post-add work. Keyed off `open`, no caller can skip it.
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError(null);
      setSuccessData(null);
      return;
    }

    if (clientSecret || isCreating) return;

    setError(null);
    setWasFirstMethod(isFirstMethod);
    createSetupIntent({})
      .then((result) => setClientSecret(result.clientSecret ?? null))
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : "Failed to initialize payment form",
        ),
      );
  }, [open]);

  const handleSuccess = useCallback(
    async (data: SuccessData) => {
      if (onSuccessProp) {
        await onSuccessProp(data);
        return;
      }

      // The card was attached through Stripe's own form, not a platform route.
      if (accountId) await refresh("billing.changed", { accountId });
      setSuccessData(data);
    },
    [onSuccessProp, refresh, accountId],
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-md sm:max-w-md flex flex-col overflow-hidden">
        <AnimatePresence mode="wait">
          {successData ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="grow flex flex-col min-h-0"
            >
              <PaymentMethodSuccessContent
                paymentMethodId={successData.paymentMethodId}
                isFirstMethod={wasFirstMethod}
                onDone={() => onOpenChange(false)}
              />
            </motion.div>
          ) : (
            <motion.div
              key="form"
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2, ease: "easeIn" }}
              className="grow flex flex-col min-h-0"
            >
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <ModalHeader>
                  <ModalTitle>
                    {mode === "edit" ? "Edit" : "Add"} payment method
                  </ModalTitle>
                </ModalHeader>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
                className="grow flex flex-col min-h-0"
              >
                {error && (
                  <div className="px-4 pb-4 pt-1">
                    <div className="rounded-lg bg-bg-dark-min p-4 text-center">
                      <Typography size="sm" className="text-text-dark-high">
                        {error}
                      </Typography>
                    </div>
                  </div>
                )}

                {isCreating && (
                  <div className="space-y-2 px-4 pb-4 pt-1">
                    <div className="flex gap-2">
                      <Skeleton className="h-[62px] w-full" />
                      <Skeleton className="h-[62px] w-full" />
                    </div>
                    <Skeleton className="h-[184px] w-full" />
                  </div>
                )}

                {clientSecret && (
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance,
                    }}
                  >
                    <PaymentMethodFormContent
                      mode={mode}
                      clientSecret={clientSecret}
                      defaultMethod={defaultMethod}
                      onSuccess={handleSuccess}
                      onError={onError}
                    />
                  </Elements>
                )}
              </motion.div>

              {!clientSecret && !isCreating && !error && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut", delay: 0.15 }}
                >
                  <ModalFooter>
                    <ModalClose asChild>
                      <Button
                        kind="outline"
                        variant="neutral"
                        className="transition-[background-color,transform] duration-150 ease-out active:scale-[0.98]"
                      >
                        Cancel
                      </Button>
                    </ModalClose>
                  </ModalFooter>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </ModalContent>
    </Modal>
  );
};

type PaymentMethodSuccessContentProps = {
  /** The newly added payment method's ID. */
  paymentMethodId: string;

  /** Whether this was the user's first payment method. */
  isFirstMethod: boolean;

  /** Callback to close the dialog. */
  onDone: () => void;
};

/**
 * The success state content shown inside the setup dialog after a payment method is added.
 * Fetches its own payment method details and offers a "Make default" action.
 *
 * @param paymentMethodId - The newly added payment method's ID.
 * @param isFirstMethod - Whether this was the user's first payment method.
 * @param onDone - Callback to close the dialog.
 */
const PaymentMethodSuccessContent = ({
  paymentMethodId,
  isFirstMethod,
  onDone,
}: PaymentMethodSuccessContentProps) => {
  const { data: paymentMethod } = usePaymentMethodDetail({
    paymentMethodId,
  }).get();
  const { data: me } = useGetAuthMe().get();
  const accountId = me?.accounts?.[0]?.id ?? "";
  const [isSetAsDefaultOpen, setIsSetAsDefaultOpen] = useState(false);
  const refresh = useRefresh();
  const { mutateAsync: setDefault } =
    useCreateBillingPaymentMethodsSetDefaultByPaymentMethodId({
      onSuccess: () => void refresh("billing.changed", { accountId }),
    });

  const brandName = paymentMethod?.card
    ? capitalize(paymentMethod.card.brand ?? "")
    : startCase(
        paymentMethod?.usBankAccount?.bankName?.toLowerCase() ??
          "Payment method",
      );
  const last4 =
    paymentMethod?.card?.last4 ?? paymentMethod?.usBankAccount?.last4 ?? "";

  useEffect(() => {
    if (isFirstMethod && paymentMethod) {
      setDefault({ params: { paymentMethodId }, body: { accountId } }).catch(
        () => {},
      );
    }
  }, [isFirstMethod, paymentMethod, setDefault]);

  const handleMakeDefault = () => setIsSetAsDefaultOpen(true);

  const handleSetDefaultSuccess = (scope: PaymentDefaultActionScope) => {
    pmToast.defaultUpdated(brandName, last4, scope);
    onDone();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle className="opacity-0">Payment method added</ModalTitle>
      </ModalHeader>

      <BlockScroll className="flex-1 min-h-0">
        <div className="flex flex-col gap-6 px-4 pb-4">
          <motion.div
            className="flex flex-col gap-1 text-center"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Typography size="xl" className="font-semibold">
              You&apos;re all set!
            </Typography>
            <Typography size="base" className="text-text-dark-high">
              You can now use it to make payments, view activity, and manage
              details in Payments.
            </Typography>
          </motion.div>

          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
          >
            {paymentMethod ? (
              <PaymentMethodCard
                brand={brandName}
                last4={last4}
                expMonth={paymentMethod.card?.expMonth}
                expYear={paymentMethod.card?.expYear}
                type={
                  paymentMethod.card
                    ? PaymentMethodType.Card
                    : PaymentMethodType.UsBankAccount
                }
                className="max-w-[360px]"
              />
            ) : (
              <Skeleton className="h-[200px] w-full max-w-[360px] rounded-2xl" />
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.15 }}
          >
            <Typography size="sm" className="text-center text-text-dark-low">
              Use of payment methods is subject to the Terms & Conditions.
            </Typography>
          </motion.div>
        </div>
      </BlockScroll>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut", delay: 0.2 }}
      >
        <ModalFooter className="flex-col">
          {!isFirstMethod && (
            <Button
              kind="solid"
              variant="primary"
              className="w-full transition-[background-color,transform] duration-150 ease-out active:scale-[0.99]"
              onClick={handleMakeDefault}
            >
              Make default
            </Button>
          )}
          <Button
            kind={isFirstMethod ? "solid" : "outline"}
            variant={isFirstMethod ? "primary" : "neutral"}
            className="w-full transition-[background-color,transform] duration-150 ease-out active:scale-[0.99]"
            onClick={onDone}
          >
            Done
          </Button>
        </ModalFooter>
      </motion.div>

      <SetAsDefaultDialog
        open={isSetAsDefaultOpen}
        onOpenChange={setIsSetAsDefaultOpen}
        paymentMethodId={paymentMethodId}
        accountId={accountId}
        onSuccess={handleSetDefaultSuccess}
      />
    </>
  );
};
